'use client';

import { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { id as idTranslations } from '@/i18n/translations/id';
import { en as enTranslations } from '@/i18n/translations/en';

export type Language = 'id' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// All translations loaded directly
const translations: Record<Language, Record<string, string>> = {
  id: idTranslations,
  en: enTranslations,
};

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>('id');
  const [mounted, setMounted] = useState(false);

  // Load language from localStorage on mount (client-side only)
  useEffect(() => {
    const savedLang = localStorage.getItem('unmul-language') as Language;
    if (savedLang && (savedLang === 'id' || savedLang === 'en')) {
      setLanguageState(savedLang);
    }
    setMounted(true);
  }, []);

  // Save language to localStorage when changed
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('unmul-language', lang);
  };

  // Translate function - memoized to prevent rerenders
  const t = useMemo(() => {
    return (key: string, params?: Record<string, string | number>): string => {
      let translation = translations[language][key];
      if (!translation) {
        // Fallback: return key if translation not found
        return key;
      }

      // Interpolate params
      if (params) {
        Object.entries(params).forEach(([paramKey, paramValue]) => {
          translation = translation.replace(new RegExp(`{${paramKey}}`, 'g'), String(paramValue));
        });
      }

      return translation;
    };
  }, [language]);

  // Prevent hydration mismatch by rendering children only after mount
  // But still render content with default language
  const value = useMemo(() => ({
    language,
    setLanguage,
    t,
  }), [language, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

// Utility hook for simple translation access
export function useTranslation() {
  const { t, language, setLanguage } = useLanguage();
  return { t, language, setLanguage };
}
